import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { ReportConfigCustomValue } from './ReportConfigCustomValue';
import type { iocContainer } from '@/engine/ioc';
import type { PhaseSwitcher } from './PhaseSwitcher';
import type { IConfigProps, IReportValueAccessor } from './interfaces';

/**
 * Represents a custom value accessor for generating reports.
 */
export class ReportCustomValueAccessor implements IReportValueAccessor {
  private _planningAlgorithm: PlanningAlgorithm;

  private _phaseSwitcher: PhaseSwitcher;

  private _ioc: typeof iocContainer;

  /**
   * Creates an instance of ReportCustomValueAccessor.
   * @param planningAlgorithm The planning algorithm.
   * @param phaseSwitcher The phase switcher.
   * @param ioc The IoC container.
   */
  public constructor(planningAlgorithm: PlanningAlgorithm, phaseSwitcher: PhaseSwitcher, ioc: typeof iocContainer) {
    this._planningAlgorithm = planningAlgorithm;
    this._phaseSwitcher = phaseSwitcher;
    this._ioc = ioc;
  }

  /**
   * Gets the value by the specified configuration entry.
   * @param configEntry The configuration entry.
   * @returns A promise that resolves to the value.
   */
  public async getValueByConfig(config: IConfigProps): Promise<unknown> {
    if (config.phase === 'endSystole' || config.phase === 'endDiastole') {
      await this._phaseSwitcher.switchPhaseByKind(config.phase);
    }

    const configEntry = config as ReportConfigCustomValue<unknown, unknown>;

    return configEntry.valueAccessor(
      { phaseSwitcher: this._phaseSwitcher, algorithm: this._planningAlgorithm, ioc: this._ioc },
      configEntry.args
    );
  }
}
